import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, Subject, map, tap } from 'rxjs';
import { BackendService } from './backend.service';
import { SKIP_SESSION_EXPIRY_REDIRECT } from '../helpers/error.interceptor';

/** One user's grant state for a connector. Metadata only — tokens live in the
 *  AgentCore Identity token vault server-side and never reach the browser. */
export interface ConnectorConnection {
  status: 'active' | 'pending' | 'disconnected' | 'revoked' | string;
  scopes: string[];
  connectedAt: number | null;
  lastVerifiedAt: number | null;
  updatedAt: number | null;
  /** 8-hex-char fingerprint of the stored credential (BYO connectors only) —
   *  enough to tell WHICH token is on file, never the token itself. */
  fingerprint: string | null;
}

/** A third-party OAuth connector (Atlassian, Salesforce, …) joined with the
 *  caller's own connection status. */
export interface Connector {
  name: string;
  displayName: string;
  description: string;
  icon: string;
  /** 'active' = users can connect; 'pending_app_registration' = provisioned
   *  in config but the vendor OAuth app isn't registered yet (coming soon). */
  connectorStatus: 'active' | 'pending_app_registration' | string;
  authMode: 'service' | 'hybrid' | 'user' | string;
  /** How a user links this connector: 'oauth2_3lo' = the provider's consent
   *  screen (the default when the API omits the field); 'user_api_key' = BYO —
   *  the user pastes a token they created in the vendor themselves. */
  credentialKind: 'oauth2_3lo' | 'user_api_key' | string;
  /** Shape the API assembles server-side, e.g. "email:api_token" (BYO only). */
  credentialFormat: string;
  /** Vendor page where the user creates/manages their token (BYO only). */
  helpUrl: string;
  /** Connector-supplied one-liner for the token form (BYO only). */
  credentialHelp: string;
  scopes: string[];
  /** Plain-language statements of what agents may do as you, supplied by the
   *  connector registry. Preferred over `scopes` for display: a vendor MCP
   *  grant is a vendor-defined bundle, so its OAuth scope list carries no
   *  user-meaningful information (Atlassian's is just `offline_access`).
   *  Empty for classic connectors, whose real scopes ARE the truth. */
  capabilities: string[];
  connection: ConnectorConnection | null;
}

export interface PutCredentialResult {
  status: 'connected' | string;
  fingerprint: string | null;
}

export interface AuthorizeResult {
  status: 'connected' | 'authorization_required' | string;
  /** Single-use, session-bound consent URL. Hand it straight to the browser;
   *  never store it. */
  authorizationUrl: string | null;
}

/**
 * Per-user OAuth connectors ("Connections") — GET/POST/DELETE /connections.
 *
 * Same conventions as me.service: thin observables, {success,message,body}
 * envelope unwrapped with defensive defaults, errors propagate to the caller
 * (the page owns retry UX). One extra: `available` — a nav-gating signal set
 * by the first list() result (the API returns an empty catalog while the
 * connectors_enabled flag is off, so catalog-non-empty IS the feature gate;
 * no flag plumbing needed client-side).
 *
 * Also owns the open channel for the global Connections modal (mounted once in
 * the layout shell, same pattern as AbilitiesService/AgentCatalogService), so
 * every surface's rail can open Connections in place instead of navigating
 * away from whatever the user was doing.
 */
@Injectable({ providedIn: 'root' })
export class ConnectionsService {
  /** null = not probed yet; false = feature dark (hide nav); true = show. */
  readonly available = signal<boolean | null>(null);
  private probeStarted = false;

  /** Emits the `session_id` to finalize with (null for a plain open). */
  private readonly _open$ = new Subject<string | null>();
  readonly open$ = this._open$.asObservable();

  /** True while the Connections modal is on screen. Owned here rather than in
   *  the component so every surface's rail can render its Connections entry as
   *  the active destination, the way a routed page would. */
  readonly modalOpen = signal(false);

  constructor(
    private readonly http: HttpClient,
    private readonly backend: BackendService,
  ) {}

  /** Open the Connections modal over the current surface.
   *
   *  `sessionUri` is only passed by the post-consent entry route: the provider
   *  redirects to /connections?session_id=… and that value must reach the API
   *  to finalize the grant. The modal is always mounted (layout shell), so a
   *  plain Subject is enough — no replay, and no risk of a stale session_id
   *  being re-applied on a later open. */
  triggerOpen(sessionUri: string | null = null): void {
    this._open$.next(sessionUri);
  }

  /** Fire-and-forget probe for nav gating — failures leave `available`
   *  untouched (nav stays hidden, never errors) AND re-arm the probe, so a
   *  transient failure on the connectors host doesn't hide Connections for
   *  the whole tab session. The probe also opts out of the interceptor's
   *  session-expiry redirect: a 401 from this secondary host must fail the
   *  probe, not sign the user out of the hub. */
  ensureProbed(): void {
    if (this.probeStarted) { return; }
    this.probeStarted = true;
    this.list(false, null, { probe: true }).subscribe({
      error: () => { this.probeStarted = false; },
    });
  }

  /** All connectors + the caller's connection status. verify=true additionally
   *  reconciles each stored status against the token vault server-side (used
   *  right after a consent redirect lands back on the Connections page).
   *
   *  sessionUri is the `session_id` AgentCore appends to the return URL after
   *  consent. Handing it back lets the API finalize the grant
   *  (CompleteResourceTokenAuth) — without it the authorization code is never
   *  exchanged and a successful consent silently never completes. */
  list(verify = false, sessionUri?: string | null, opts?: { probe?: boolean }): Observable<Connector[]> {
    const qs = new URLSearchParams();
    if (verify) { qs.set('verify', '1'); }
    if (sessionUri) { qs.set('session_id', sessionUri); }
    const query = qs.toString();
    const url = this.backend.createConnectorsUrl(`connections${query ? `?${query}` : ''}`);
    const context = opts?.probe
      ? new HttpContext().set(SKIP_SESSION_EXPIRY_REDIRECT, true)
      : undefined;
    return this.http.get<{ body?: unknown }>(url, context ? { context } : {}).pipe(
      map(res => (Array.isArray(res?.body) ? res.body : []).map(raw => this.normalize(raw as Record<string, unknown>))),
      tap(list => {
        this.available.set(list.length > 0);
        this.names = new Map(list.map(c => [c.name, c.displayName]));
      }),
    );
  }

  /** Human name for a connector, for surfaces that may render one before the
   *  catalog has loaded (e.g. the in-chat connection_required card). Falls
   *  back to a prettified registry name, never the raw slug. */
  displayNameFor(name: string): string {
    const known = this.names.get(name);
    if (known) { return known; }
    return (name || '')
      .split('-')
      .map(w => (w ? w[0].toUpperCase() + w.slice(1) : w))
      .join(' ');
  }
  private names = new Map<string, string>();

  /** The post-consent `session_id` survives detours (a /login bounce, a failed
   *  navigation) in per-tab sessionStorage — the same store the hub session
   *  uses, so it lives and dies with the consent tab. Losing this value means
   *  the authorization code is never exchanged and the user's successful
   *  consent silently evaporates: they'd have to re-consent. */
  private static readonly PENDING_SESSION_KEY = 'wg-connections-pending-session';

  stashPendingSession(sessionUri: string): void {
    try { sessionStorage.setItem(ConnectionsService.PENDING_SESSION_KEY, sessionUri); } catch { /* storage full/blocked — best effort */ }
  }

  peekPendingSession(): string | null {
    try { return sessionStorage.getItem(ConnectionsService.PENDING_SESSION_KEY); } catch { return null; }
  }

  clearPendingSession(): void {
    try { sessionStorage.removeItem(ConnectionsService.PENDING_SESSION_KEY); } catch { /* best effort */ }
  }

  /** Start (or verify) the OAuth consent flow for one connector. */
  authorize(name: string, force = false): Observable<AuthorizeResult> {
    const url = this.backend.createConnectorsUrl(`connections/${encodeURIComponent(name)}/authorize`);
    return this.http.post<{ body?: Record<string, unknown> }>(url, force ? { force: true } : {}).pipe(
      map(res => ({
        status: String(res?.body?.['status'] ?? ''),
        authorizationUrl: (res?.body?.['authorization_url'] as string | undefined) ?? null,
      })),
    );
  }

  /** Store + validate a user-pasted credential for a BYO (user_api_key)
   *  connector. The token transits straight to the API over TLS and lands in
   *  the encrypted vault server-side — it is never persisted client-side and
   *  never comes back down. Error statuses the caller should map: 400 =
   *  vendor rejected it (the thrown `message` says why), 502/503 = the vendor
   *  couldn't be reached to validate right now. */
  putCredential(connector: string, credential: string, email: string): Observable<PutCredentialResult> {
    const url = this.backend.createConnectorsUrl(`connections/${encodeURIComponent(connector)}/credential`);
    return this.http.post<{ body?: Record<string, unknown> }>(url, { credential, email }).pipe(
      map(res => ({
        status: String(res?.body?.['status'] ?? ''),
        fingerprint: (res?.body?.['fingerprint'] as string | undefined) ?? null,
      })),
    );
  }

  /** Disconnect — the server marks the grant revoked; the next authorize
   *  forces full re-consent. For BYO connectors this also deletes the stored
   *  credential server-side. */
  disconnect(name: string): Observable<void> {
    const url = this.backend.createConnectorsUrl(`connections/${encodeURIComponent(name)}`);
    return this.http.delete(url).pipe(map(() => void 0));
  }

  private normalize(raw: Record<string, unknown>): Connector {
    const conn = raw['connection'] as Record<string, unknown> | null | undefined;
    return {
      name: String(raw['name'] ?? ''),
      displayName: String(raw['display_name'] ?? raw['name'] ?? ''),
      description: String(raw['description'] ?? ''),
      icon: String(raw['icon'] ?? ''),
      connectorStatus: String(raw['connector_status'] ?? ''),
      authMode: String(raw['auth_mode'] ?? 'service'),
      // Older API builds don't send credential_kind — those connectors are all
      // OAuth, so the default keeps them on the unchanged consent flow.
      credentialKind: String(raw['credential_kind'] ?? 'oauth2_3lo'),
      credentialFormat: String(raw['credential_format'] ?? ''),
      helpUrl: String(raw['help_url'] ?? ''),
      credentialHelp: String(raw['credential_help'] ?? ''),
      scopes: Array.isArray(raw['scopes']) ? (raw['scopes'] as string[]) : [],
      capabilities: Array.isArray(raw['capabilities']) ? (raw['capabilities'] as string[]) : [],
      connection: conn
        ? {
            status: String(conn['status'] ?? ''),
            scopes: Array.isArray(conn['scopes']) ? (conn['scopes'] as string[]) : [],
            connectedAt: (conn['connected_at'] as number | undefined) ?? null,
            lastVerifiedAt: (conn['last_verified_at'] as number | undefined) ?? null,
            updatedAt: (conn['updated_at'] as number | undefined) ?? null,
            fingerprint: (conn['fingerprint'] as string | undefined) ?? null,
          }
        : null,
    };
  }
}
